import type { ColumnDef } from "@tanstack/react-table";
import { actionId, type TableRowMenu } from "@/hooks/useTableRowMenu";
import { buildActionColumn, buildTwoLineColumn } from "@/components/Table";
import type { Action } from "@/components/MenuButton";
import type { MaterialNames } from "@/components/material_symbol_names";
import {
  sectionConsultantRoleLabel,
  type CoverageGroupBy,
  type CoverageGroupMember,
  type CoverageGroupRow
} from "../types";
import { coverageMembersCsv, formatCoveragePrimary } from "./groupCoverage";

type GroupColumnsOptions = {
  groupBy: CoverageGroupBy;
  groupColumnHeader: string;
  addActionLabel: string;
  memberAddActionLabel: string;
  removeActionLabel: string;
  removeActionIconName?: MaterialNames;
  archiveRolesActionLabel?: string;
  setMenu: (menu: TableRowMenu) => void;
  onAdd: (group: CoverageGroupRow) => void;
  onAddMember: (member: CoverageGroupMember) => void;
  onRemoveMember?: (member: CoverageGroupMember) => void;
  onArchiveRoles?: (group: CoverageGroupRow) => void;
};

function groupOverflowActions({
  group,
  groupBy,
  addActionLabel,
  memberAddActionLabel,
  removeActionLabel,
  removeActionIconName = "delete",
  archiveRolesActionLabel,
  onAdd,
  onAddMember,
  onRemoveMember,
  onArchiveRoles
}: {
  group: CoverageGroupRow;
  groupBy: CoverageGroupBy;
  addActionLabel: string;
  memberAddActionLabel: string;
  removeActionLabel: string;
  removeActionIconName: MaterialNames;
  archiveRolesActionLabel?: string;
  onAdd: (group: CoverageGroupRow) => void;
  onAddMember: (member: CoverageGroupMember) => void;
  onRemoveMember?: (member: CoverageGroupMember) => void;
  onArchiveRoles?: (group: CoverageGroupRow) => void;
}): Action[] {
  const actions: Action[] = [];

  if (group.canAdd) {
    actions.push({
      text: addActionLabel,
      onClick: () => onAdd(group)
    });
  }

  if (archiveRolesActionLabel && onArchiveRoles && group.members.some((member) => member.assignmentId)) {
    actions.push({
      text: archiveRolesActionLabel,
      iconName: removeActionIconName,
      destructive: true,
      onClick: () => onArchiveRoles(group)
    });
  } else {
    for (const member of group.members) {
      if (member.person) {
        const targetName = groupBy === "consultant" ? member.sectionName : member.person.name;
        const roleSuffix = member.roleType ? ` (${sectionConsultantRoleLabel(member.roleType)})` : "";
        actions.push({
          text: `${removeActionLabel} ${targetName}${roleSuffix}`,
          iconName: removeActionIconName,
          destructive: true,
          onClick: () => onRemoveMember?.(member)
        });
        continue;
      }

      actions.push({
        text: `${memberAddActionLabel} — ${member.sectionName}`,
        onClick: () => onAddMember(member)
      });
    }
  }

  return actions;
}

export function getCoverageGroupColumns({
  groupBy,
  groupColumnHeader,
  addActionLabel,
  memberAddActionLabel,
  removeActionLabel,
  removeActionIconName = "delete",
  archiveRolesActionLabel,
  setMenu,
  onAdd,
  onAddMember,
  onRemoveMember,
  onArchiveRoles
}: GroupColumnsOptions): ColumnDef<CoverageGroupRow, unknown>[] {
  return [
    {
      ...buildTwoLineColumn<CoverageGroupRow>({
        accessorKey: "label",
        header: groupColumnHeader,
        getPrimaryText: (row) => formatCoveragePrimary(row.label, row.location),
        getSecondaryText: (row) => coverageMembersCsv(row, groupBy),
        size: 480
      }),
      enableSorting: false
    },
    buildActionColumn<CoverageGroupRow>({
      getActions: (row) =>
        groupOverflowActions({
          group: row,
          groupBy,
          addActionLabel,
          memberAddActionLabel,
          removeActionLabel,
          removeActionIconName,
          archiveRolesActionLabel,
          onAdd,
          onAddMember,
          onRemoveMember,
          onArchiveRoles
        }),
      actionId,
      setMenu
    })
  ];
}
